import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

const STICKY_FOOTER_HIDE_ROOT_MARGIN_TOP_PX = 200;

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} [quantityDisplay] - Quantity display container (may be absent during morph)
 * @property {HTMLElement} [quantityNumber] - Quantity number element (may be absent during morph)
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #hiddenByTryNowGate = false;

  /** @type {MutationObserver | null} */
  #tryNowGateObserver = null;

  /** @type {WeakSet<Element>} */
  #tryNowToggleBindings = new WeakSet();

  /** @type {boolean} */
  #pendingStickyFlyAnimation = false;

  /** @type {string | null} */
  #pendingStickyVariantId = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#setupTryNowGateObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartFinish, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartFinish, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#tryNowGateObserver?.disconnect();
    this.#tryNowGateObserver = null;
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: `${STICKY_FOOTER_HIDE_ROOT_MARGIN_TOP_PX}px 0px 0px 0px`,
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * TryNow injects `gated-flow-toggle` with `.tn-gate-toggle-input`. When checked (Try Before You Buy on),
   * hide the sticky bar so it does not duplicate the Try flow UI.
   */
  #setupTryNowGateObserver() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const scanAndBind = () => {
      const toggle = section.querySelector('gated-flow-toggle');
      if (!toggle) {
        if (this.#hiddenByTryNowGate) {
          this.#applyTryNowGateVisibility();
        }
        return;
      }
      this.#bindTryNowGateToggle(toggle);
    };

    scanAndBind();

    this.#tryNowGateObserver = new MutationObserver(() => scanAndBind());
    this.#tryNowGateObserver.observe(section, { childList: true, subtree: true });
  }

  /**
   * @param {Element} toggle
   */
  #bindTryNowGateToggle(toggle) {
    if (this.#tryNowToggleBindings.has(toggle)) return;

    const root =
      toggle instanceof HTMLElement && toggle.shadowRoot instanceof ShadowRoot
        ? toggle.shadowRoot
        : toggle;
    const input = root.querySelector('.tn-gate-toggle-input');
    if (!(input instanceof HTMLInputElement)) return;

    this.#tryNowToggleBindings.add(toggle);
    input.addEventListener('change', () => this.#applyTryNowGateVisibility(), {
      signal: this.#abortController.signal,
    });
    this.#applyTryNowGateVisibility();
  }

  #getTryNowGateInputInSection() {
    const section = this.closest('.shopify-section');
    const toggle = section?.querySelector('gated-flow-toggle');
    if (!toggle) return null;

    const root =
      toggle instanceof HTMLElement && toggle.shadowRoot instanceof ShadowRoot
        ? toggle.shadowRoot
        : toggle;
    const input = root.querySelector('.tn-gate-toggle-input');
    return input instanceof HTMLInputElement ? input : null;
  }

  #applyTryNowGateVisibility() {
    const input = this.#getTryNowGateInputInSection();

    if (input?.checked) {
      this.#hiddenByTryNowGate = true;
      if (this.#isStuck) this.#hideStickyBar();
      return;
    }

    this.#hiddenByTryNowGate = false;
    this.#maybeRestoreStickyAfterTryNowGateOff();
  }

  #getFooterElement() {
    return document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
  }

  #footerIntersectsStickyHideRoot() {
    const footer = this.#getFooterElement();
    if (!footer) return false;

    const rect = footer.getBoundingClientRect();
    const rootTop = -STICKY_FOOTER_HIDE_ROOT_MARGIN_TOP_PX;
    const rootBottom = window.innerHeight;
    const overlapsVertically = rect.top < rootBottom && rect.bottom > rootTop;
    const overlapsHorizontally = rect.left < window.innerWidth && rect.right > 0;
    return overlapsHorizontally && overlapsVertically;
  }

  #maybeRestoreStickyAfterTryNowGateOff() {
    if (this.#hiddenByBottom) return;

    const productForm = this.#getProductForm();
    const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    if (this.#footerIntersectsStickyHideRoot()) {
      this.#hiddenByBottom = true;
      return;
    }

    const rect = buyButtonsBlock.getBoundingClientRect();
    const scrolledPastBuyButtons = rect.bottom < 0 || rect.top < 0;
    if (scrolledPastBuyButtons) {
      this.#showStickyBar();
    }
  }

  async #runStickyAddSuccessAnimation() {
    const stickyBar = this.refs.stickyBar;
    const addToCartButton =
      stickyBar?.querySelector('[ref="addToCartButton"]') ?? this.refs.addToCartButton;
    const productImage = stickyBar?.querySelector('[ref="productImage"]') ?? this.refs.productImage;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!(addToCartButton instanceof HTMLButtonElement)) return;

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyEnabled = this.dataset.addToCartAnimation === 'true';
    const imageUrl =
      productImage instanceof HTMLImageElement ? productImage.currentSrc || productImage.src : '';
    if (flyEnabled && cartIcon && imageUrl) {
      const flyToCartElement = /** @type {HTMLElement & { source: Element; destination: Element; useSourceSize: string | boolean }} */ (
        document.createElement('fly-to-cart')
      );
      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${imageUrl})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = /** @type {HTMLImageElement} */ (productImage);
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
      await onAnimationEnd([addToCartButton, flyToCartElement]);
    } else {
      await onAnimationEnd(addToCartButton);
    }

    this.#resetTimeout = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
    }, 800);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.dataset.kickstarter === 'true') return;
    if (!this.#targetAddToCartButton) return;

    if (
      window.__styluxAtcInterceptEnabled === true &&
      typeof window.__styluxOpenOfferModalForVariant === 'function'
    ) {
      const form = this.#targetAddToCartButton.closest('form[data-type="add-to-cart-form"]');
      const productForm = form?.closest('product-form-component');
      if (
        form &&
        productForm instanceof HTMLElement &&
        productForm.getAttribute('data-kickstarter-mode') !== 'true'
      ) {
        const variantInput = form.querySelector('input[name="id"]');
        if (
          variantInput instanceof HTMLInputElement &&
          !variantInput.disabled &&
          variantInput.value
        ) {
          this.#pendingStickyFlyAnimation = true;
          this.#pendingStickyVariantId = variantInput.value;
          void window.__styluxOpenOfferModalForVariant(variantInput.value, this.#targetAddToCartButton);
          return;
        }
      }
    }

    const nativeForm = this.#targetAddToCartButton.closest('form[data-type="add-to-cart-form"]');
    const nativeVariantInput = nativeForm?.querySelector('input[name="id"]');
    const nativeVariantId =
      nativeVariantInput instanceof HTMLInputElement && nativeVariantInput.value
        ? nativeVariantInput.value
        : this.dataset.currentVariantId || null;
    this.#pendingStickyFlyAnimation = true;
    this.#pendingStickyVariantId = nativeVariantId;

    this.#targetAddToCartButton.dataset.puppet = 'true';
    if (typeof window !== 'undefined') {
      window.__stickySuppressNextFlyToCart = true;
      queueMicrotask(() => {
        if (window.__stickySuppressNextFlyToCart === true) {
          window.__stickySuppressNextFlyToCart = false;
        }
      });
    }
    this.#targetAddToCartButton.click();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    const eventProductId = event.detail.data.productId;
    const ourProductId = this.dataset.productId;
    const morphedMain = event.detail.data.morphedMain === true;
    if (eventProductId !== ourProductId && !morphedMain) return;

    const variant = event.detail.resource;

    if (morphedMain) {
      this.#isStuck = false;
      this.#hiddenByBottom = false;
      if (this.refs.stickyBar) this.refs.stickyBar.setAttribute('data-stuck', 'false');
      if (variant?.id) this.dataset.currentVariantId = variant.id;
      if (eventProductId) this.dataset.productId = eventProductId;
      this.#buyButtonsIntersectionObserver?.disconnect();
      this.#mainBottomObserver?.disconnect();
      this.#setupIntersectionObserver();
      const productForm = this.#getProductForm();
      if (productForm) this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
      this.refs.stickyBar?.querySelector('[ref="addToCartButton"]')?.removeAttribute('data-added');
      if (variant == null) this.#handleVariantUnavailable();
      this.#updateButtonText();
      this.#applyTryNowGateVisibility();
      return;
    }

    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar?.querySelector('[ref="addToCartButton"]')?.removeAttribute('data-added');
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;
    if (newStickyAddToCart.dataset.productId) this.dataset.productId = newStickyAddToCart.dataset.productId;
    if (newStickyAddToCart.dataset.addToCartAnimation !== undefined) {
      this.dataset.addToCartAnimation = newStickyAddToCart.dataset.addToCartAnimation;
    }

    if (variant?.id) this.dataset.currentVariantId = variant.id;

    const productForm = this.#getProductForm();
    if (productForm) this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    if (variant == null) this.#handleVariantUnavailable();
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * @param {Event} event
   */
  #clearPendingStickySuccess() {
    this.#pendingStickyFlyAnimation = false;
    this.#pendingStickyVariantId = null;
  }

  #handleCartFinish = (event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (!this.#pendingStickyFlyAnimation) return;

    if (event.type === ThemeEvents.cartError) {
      this.#clearPendingStickySuccess();
      return;
    }

    const didError = event.detail?.data?.didError === true;
    if (didError) {
      this.#clearPendingStickySuccess();
      return;
    }

    const vid = this.#pendingStickyVariantId || this.dataset.currentVariantId;
    const sourceId = event.detail?.sourceId;
    const dataSource = event.detail?.data?.source;
    const fromStylux = dataSource === 'stylux' || sourceId === 'stylux';
    const fromSameVariant = Boolean(vid && sourceId && String(sourceId) === String(vid));
    const fromProductForm = dataSource === 'product-form-component';
    const variantIdsFromBatch = event.detail?.data?.variantIds;
    const fromProductFormBatchVariant =
      fromProductForm &&
      Boolean(vid) &&
      Array.isArray(variantIdsFromBatch) &&
      variantIdsFromBatch.some((batchVid) => String(batchVid) === String(vid));

    const cart = event.detail?.resource;
    const matchesOurVariant =
      Boolean(vid) &&
      Array.isArray(cart?.items) &&
      cart.items.some((it) => String(it.variant_id) === String(vid));

    const shouldAnimate =
      (fromProductForm && fromSameVariant) ||
      fromProductFormBatchVariant ||
      (fromStylux && matchesOurVariant);

    this.#clearPendingStickySuccess();
    if (!shouldAnimate) return;
    void this.#runStickyAddSuccessAnimation();
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    if (this.#hiddenByTryNowGate) return;

    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    if (!addToCartButton) return;

    if (this.dataset.kickstarter === 'true') {
      if (quantityDisplay) quantityDisplay.style.display = 'none';
      return;
    }

    const available = !addToCartButton.disabled;

    if (quantityNumber) quantityNumber.textContent = this.#currentQuantity.toString();

    if (quantityDisplay) {
      quantityDisplay.style.display = available && this.#currentQuantity > 1 ? 'inline' : 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
